// ─── CGT form type identifiers ───────────────────────────────────────────────

export type CgtFormType =
  | 'chart'                       // patient demographics — always first
  | 'transplant'                  // procedure definition — requires chart
  | 'donor'                       // donor record — requires transplant
  | 'collection_processing_order' // what to collect and how — requires donor
  | 'collection'                  // the actual collection event — requires CPO
  | 'processing'                  // lab processing steps — requires collection
  | 'product_release_review'      // QA sign-off — requires processing
  | 'distribution'                // transfer out or infusion — requires release

/** Human-readable labels for each CGT form type */
export const CGT_FORM_LABELS: Record<CgtFormType, string> = {
  chart: 'Patient Chart',
  transplant: 'Transplant Order',
  donor: 'Donor Record',
  collection_processing_order: 'Collection & Processing Order',
  collection: 'Collection',
  processing: 'Processing',
  product_release_review: 'Product Release Review',
  distribution: 'Distribution',
}

/** Natural ordering of CGT forms in the workflow */
export const CGT_FORM_ORDER: CgtFormType[] = [
  'chart',
  'transplant',
  'donor',
  'collection_processing_order',
  'collection',
  'processing',
  'product_release_review',
  'distribution',
]

/** Logical predecessor defaults per form type */
export const CGT_DEFAULT_PREDECESSORS: Partial<Record<CgtFormType, CgtFormType[]>> = {
  transplant: ['chart'],
  donor: ['transplant'],
  collection_processing_order: ['donor'],
  collection: ['collection_processing_order'],
  processing: ['collection'],
  product_release_review: ['processing'],
  distribution: ['product_release_review'],
}

// ─── Field types supported in the CGT form engine ────────────────────────────

export type CgtFieldType =
  | 'text'
  | 'number'
  | 'date'
  | 'datetime' // date + time in one field; stored as YYYY-MM-DDTHH:mm (naive local, no timezone)
  | 'textarea'
  | 'checkbox'
  | 'dropdown'
  | 'calculated' // read-only, derived from other fields via a formula string
  | 'signature'  // requires the submitting user to belong to a specific group
  | 'process'    // anchors a Process step (Inventory/Equipment/Staffing config)
  | 'section'    // layout divider — heading + separator, no value
  | 'table'      // inline table grid; carries its own tableConfig
  | 'image'      // a picture IS the value: stored as a FormAttachment, rendered inline
  | 'attachment' // any number of documents ARE the value: FormAttachment rows, one per slot (z8nrz7dp7p)
  | 'description' // fixed text the AUTHOR writes; read-only at fill time, stores nothing
  // Review sign-off (z8nrz7dt41): rendered in the grey Review area at the
  // bottom of the form, never in its section. Assigned while the form is
  // filled, e-signed by the assignee once it is completed. Stores nothing in
  // `values` — its assignment and signature are FormReview rows.
  | 'review'

export type FieldStatus = 'empty' | 'filled' | 'locked' // locked = after signature applied

// ─── MR (Medical Record) fields ──────────────────────────────────────────────
// An MR field's value is never typed by the user or stored in submission.values —
// the renderer populates it read-only from the Patient row linked to the
// transplant. `mrFieldKey` names the Patient demographic column it reads.

export type MrFieldKey =
  | 'mrn'
  | 'firstName'
  | 'lastName'
  | 'fullName'
  | 'sex'
  | 'birthDate'
  | 'email'
  | 'phone'
  | 'addressLine'
  | 'city'
  | 'state'
  | 'postalCode'
  | 'country'
  | 'preferredLanguage'

export const MR_FIELD_LABELS: Record<MrFieldKey, string> = {
  mrn: 'MRN',
  firstName: 'First Name',
  lastName: 'Last Name',
  fullName: 'Full Name',
  sex: 'Sex',
  birthDate: 'Date of Birth',
  email: 'Email',
  phone: 'Phone',
  addressLine: 'Street Address',
  city: 'City',
  state: 'State',
  postalCode: 'Postal Code',
  country: 'Country',
  preferredLanguage: 'Preferred Language',
}

export const MR_FIELD_KEYS = Object.keys(MR_FIELD_LABELS) as MrFieldKey[]

// ─── Conditional visibility ───────────────────────────────────────────────────
// Value-driven show/hide rule on a field or section — distinct from the
// unlockedBy / gatedByPrevious sequencing mechanisms (those enable/disable;
// this renders/hides). Expression is a two-level tree: groups joined by the
// rule's `logic`, conditions inside a group joined by the group's own `logic`.
// Grouping is the only precedence mechanism (one connector per level).
// Mirrors cgt-backend src/form-engine/evaluate-visibility.ts — keep in sync.

export type VisibilityOperator =
  | 'equals'
  | 'notEquals'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'contains'   // case-insensitive substring
  | 'startsWith' // case-insensitive prefix
  | 'endsWith'   // case-insensitive suffix

export interface VisibilityCondition {
  sourceFieldId: string    // the field whose value drives this condition
  operator: VisibilityOperator // gt/gte/lt/lte valid only for number/date/calculated sources
  value: string | number   // compared against the source's live value
  // Escape hatch: when set, this row ignores sourceFieldId/operator/value and
  // instead evaluates this Boolean formula (same grammar as calculated-field
  // formulas — see lib/formula.ts's evaluateGateFormula), fails CLOSED. Lets a
  // condition express things the structured field/operator/value form can't,
  // e.g. date arithmetic (`TODAY() <= {expiration}`). Composes normally with
  // other rows via the group's AND/OR — this is a per-condition mode, not a
  // parallel mechanism.
  formula?: string
}

export interface VisibilityGroup {
  logic: 'and' | 'or'      // connector joining the conditions in this group
  conditions: VisibilityCondition[]
}

export interface VisibilityRule {
  logic: 'and' | 'or'      // connector joining the groups
  groups: VisibilityGroup[] // bracketed condition groups (one level)
  action: 'show' | 'hide'  // show = hidden until the expression is true; hide = hidden while true
  require?: boolean        // mark the target required while it is visible
}

// ─── Conditional notification (fires on form submit) ─────────────────────────
// Independent, parallel rule to VisibilityRule — same {logic, groups}
// condition tree, different action payload (message/channels/recipients
// instead of show/hide). A field may carry visibility, notification, both, or
// neither; they never interfere with each other. FIELD ONLY — not sections.
// Evaluated ONLY server-side, at submission completion (cgt-backend
// src/form-submissions/form-submissions.service.ts `complete()`) — there is
// no client-side evaluator for this rule, only the authoring UI + sanitizer
// (see sanitizeNotificationRule in lib/formSections.ts). Mirrors cgt-backend
// src/form-engine/evaluate-notification-rules.ts — keep in sync.

export type NotificationChannel = 'on_screen' | 'email'

export type NotificationRecipients =
  | { mode: 'all' }
  | { mode: 'members'; userIds: string[] }
  // Addressed by ROLE rather than by person, resolved to whoever holds it at
  // SEND time — "every QA reviewer", not four named users who each have to be
  // re-picked when someone joins or leaves.
  | { mode: 'roles'; roleIds: string[] }

export interface NotificationRule {
  logic: 'and' | 'or'
  groups: VisibilityGroup[] // reuses the VisibilityGroup/VisibilityCondition shape verbatim
  message: string
  channels: NotificationChannel[] // 'on_screen' and/or 'email' — at least one
  recipients: NotificationRecipients
}

// ─── Formula-based gates ───────────────────────────────────────────────────
// A Boolean formula (same grammar as calculated-field formulas — see
// lib/formula.ts's evaluateGateFormula) that must evaluate true before the
// gated thing is allowed. Fails CLOSED: a blank/malformed formula locks.
// Distinct from VisibilityRule (a structured condition-group builder that
// compares a field to a literal) — a gate can express arbitrary formulas,
// including date arithmetic like `TODAY() <= {expiration}`.
// Mirrors cgt-backend src/form-engine's FormulaGate shape — keep in sync.

export interface FormulaGate {
  formula: string
  /** Shown to the user when the gate evaluates false. */
  message?: string
}

// ─── Per-field attachment ─────────────────────────────────────────────────────
// An add-on any VALUE field can opt into — deliberately not a CgtFieldType, so a
// Number field can carry its instrument printout and a Signature field its signed
// PDF. The uploaded file lives in the FormAttachment table joined on
// (submissionId, fieldId), never in the flat FormSubmission.values map.
// Mirrors cgt-backend src/form-submissions/attachment-config.ts — keep in sync.
// See https://app.clickup.com/t/86eyfh0cc.

export interface AttachmentConfig {
  enabled: boolean
  /** Blocks form completion until a file is attached — same weight as `required`. */
  required: boolean
}

/**
 * How the authoring UI presents the config: one three-value choice, not two
 * independent switches. `AttachmentConfig` stays the stored shape — 'none' is
 * simply the absent config — so this is a presentation concern only.
 */
export type AttachmentMode = 'none' | 'enabled' | 'required'

export const ATTACHMENT_MODE_LABELS: Record<AttachmentMode, string> = {
  none: 'None',
  enabled: 'Enabled',
  required: 'Required',
}

export const ATTACHMENT_MODES = Object.keys(ATTACHMENT_MODE_LABELS) as AttachmentMode[]

export function attachmentMode(attachment: AttachmentConfig | undefined): AttachmentMode {
  if (!attachment?.enabled) return 'none'
  return attachment.required ? 'required' : 'enabled'
}

export function attachmentFromMode(mode: AttachmentMode): AttachmentConfig | undefined {
  if (mode === 'none') return undefined
  return { enabled: true, required: mode === 'required' }
}

/** Accepted upload types and size ceiling. The single source of truth for BOTH
 *  the client-side picker filter and the server-side presign guard — mirrored in
 *  the backend constant of the same name. */
export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024

export const ATTACHMENT_ACCEPTED_MIME: readonly string[] = [
  'image/jpeg',
  'image/png',
  'application/pdf',
]

/**
 * Whether a field type can carry a per-field attachment. Takes a plain string so
 * both `CgtFieldType` and the builder's own `FieldType` union can be passed
 * without a cast (they differ only in dropdown/select, neither of which matters
 * here). The exclusions deliberately mirror the `required` toggle's: a section
 * holds no value, a calculated field is derived, and table cells are out of
 * scope for attachments.
 */
export function supportsAttachment(fieldType: string): boolean {
  if (valueIsAttachment(fieldType)) return false
  return fieldType !== 'section' && fieldType !== 'calculated' && fieldType !== 'table'
}

/**
 * Field types whose VALUE is an uploaded file rather than an entry in
 * `FormSubmission.values`.
 *
 * `image` is the only one, and it is deliberately not an ordinary field with
 * `attachment.enabled` turned on. The add-on attaches evidence BESIDE a value —
 * an instrument printout next to the number it produced — and can be switched
 * off without the field losing its meaning. Here the file is the whole point:
 * there is no second value to carry, nothing to show when the add-on is off,
 * and `required` has to mean "a picture is on file" rather than "values[id] is
 * non-empty". So `supportsAttachment` returns FALSE for it — the builder never
 * offers the toggle — while the storage path underneath is the same
 * FormAttachment row keyed on (submissionId, fieldId).
 *
 * Mirrors cgt-backend src/form-submissions/attachment-config.ts — keep in sync.
 */
export function valueIsAttachment(fieldType: string): boolean {
  return fieldType === 'image' || isMultiFileAttachment(fieldType)
}

/**
 * The Attachment field type (z8nrz7dp7p): a labelled list of documents that
 * ARE the field's value. There is no limit on how many.
 *
 * Unlike an Image field it holds several rows, one per slot, so each file is
 * addressed by its row id and an upload adds a file rather than replacing one.
 * Same accepted types and size ceiling as the add-on.
 *
 * Mirrors cgt-backend src/form-submissions/attachment-config.ts — keep in sync.
 */
export function isMultiFileAttachment(fieldType: string): boolean {
  return fieldType === 'attachment'
}


/**
 * Field types whose content is fixed by the AUTHOR at build time and read-only
 * to whoever fills the form.
 *
 * A `description` holds a sentence the form always says — a standing
 * instruction, a caveat, the reference range a nearby number is read against.
 * The text lives in the DEFINITION (`constantText`), never in
 * `FormSubmission.values`, so it is the same on every submission and there is
 * nothing to save, validate or audit.
 *
 * It is not a `section`, which is a divider with a heading and no body, and not
 * `helpText`, which hides inside a tooltip. This is body text that is always on
 * screen.
 *
 * Consequences that follow, and are why this predicate exists rather than a
 * scatter of `=== 'description'`: it can never be required, can never be a
 * prerequisite or a visibility source, is never write-once, and is skipped by
 * the required-value check on completion.
 *
 * Mirrors cgt-backend src/form-engine/constant-fields.ts — keep in sync.
 */
export function isConstantField(fieldType: string): boolean {
  return fieldType === 'description'
}

/** MIME types an image field accepts — the attachment list, minus the documents. */
export const IMAGE_FIELD_ACCEPTED_MIME: readonly string[] = ['image/jpeg', 'image/png']

/** Drop a config that a field type can't carry, and clear `required` whenever
 *  the attachment itself is off — never leave an orphaned required flag. */
export function normalizeAttachment(
  fieldType: string,
  attachment: AttachmentConfig | undefined,
): AttachmentConfig | undefined {
  if (!attachment?.enabled || !supportsAttachment(fieldType)) return undefined
  return { enabled: true, required: !!attachment.required }
}

// ─── Out-of-specification grading (z8nrz7eq4e, z8nrz7eq4h) ──────────────────
// Semantics live in lib/oos.ts, mirrored from cgt-backend form-engine/evaluate-oos.ts.
// Number fields grade by range (low/high + band min/max). Text, dropdown,
// checkbox and date fields grade by the first band whose `when` matches.

export type OosSeverity = 'low' | 'medium' | 'high'

/** The comparisons a `when` rule can make. Same semantics as VisibilityOperator. */
export type OosOperator =
  | 'equals'
  | 'notEquals'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'contains'
  | 'startsWith'
  | 'endsWith'

export interface OosBand {
  min?: number             // number only: inclusive start; absent = open below
  max?: number             // number only: exclusive end; absent = open above
  /** Non-number types only: the value this band grades. */
  when?: { operator: OosOperator; value: string }
  severity: OosSeverity
  color?: string           // #rrggbb; absent = the severity default
  commentRequired: boolean
}

export interface OosConfig {
  low?: number             // number only: inclusive in-spec low limit
  high?: number            // number only: inclusive in-spec high limit
  bands: OosBand[]         // tried in order; first match grades the value
  alertGroupIds: string[]  // UserGroup ids alerted on an out-of-spec value
  /** Email the alert groups too. Absent = off. */
  emailGroups?: boolean
}

// ─── Form definition (what the builder produces and saves) ───────────────────

export interface FieldDefinition {
  id: string
  label: string
  /**
   * The laboratory (LIS) stage this field belongs to: a stage NAME, as an order
   * records it. A lab result mapped here is written only for an order placed at
   * this stage. Absent means no stage — the default, written whatever the stage
   * unless another destination for the same value is set to the order's stage.
   */
  lisStage?: string | null
  /**
   * The LIS test code this field is connected to (z8nrz7g2wm), e.g. `TBNK`,
   * shown as a label beneath the field. A label only: it does not limit what
   * the LIS writes here, which is the integration's mapping. Not the
   * observation code a pre-mapping matches (`CD3PCT`): that one says which
   * value, this one which test it represents. Absent means none.
   */
  lisTestCode?: string | null
  fieldType: CgtFieldType
  required: boolean
  helpText?: string
  order: number
  colSpan?: 1 | 2          // deprecated: use fieldWidth. 1 = half, 2 = full
  fieldWidth?: SectionWidth // width in the section's 12-col grid; supersedes colSpan
  /** Where the label sits beside the control (z8nrz7djyx). Absent = the form's fieldLayout, then 'left'. */
  labelPosition?: LabelPosition
  /** Label column width when the label sits left or right. Absent = the form's fieldLayout, then 'narrow'. */
  labelWidth?: LabelWidth
  options?: string[]       // for dropdown only — inline options
  optionSetId?: string     // for dropdown only — id of a referenced Option Set (replaces inline options)
  formula?: string         // for calculated only — {fieldId} tokens, e.g. "{id_a} / {id_b}"
  formulaRefs?: string[]   // for calculated only — denormalized operand field IDs
  displayFormat?: 'number' | 'decimal2' | 'decimal3' | 'date' | 'datetime' // for calculated only
  requiredGroup?: string   // LEGACY label, never enforced — superseded by allowedSignerGroupIds
  /** Signature only: UserGroup ids whose members may sign (z8nrz7dt45). Empty = anyone. Enforced server-side. */
  allowedSignerGroupIds?: string[]
  /** Signature only: fields sharing this set name must be signed by different people (z8nrz7dt44). */
  distinctSignerSet?: string
  isGate?: boolean         // if true, filling this field unlocks a downstream form
  gatedFormType?: CgtFormType
  anchored?: boolean       // if true, surfaced in the procedure header across all procedure forms
  // Write-once entry. Absent or true means the field stays editable for the life
  // of the submission (which is what EVERY definition saved before this property
  // existed means, so `undefined` must never be read as locked). An explicit
  // `false` makes the field permanently read-only the moment it holds a value —
  // see `isFieldWriteLocked` in lib/fieldLock.ts for what counts as "holds a
  // value", and note the backend re-checks the same rule in saveFieldValue.
  canChange?: boolean
  enabled?: boolean        // library field only — false hides it from insertion pickers; already-inserted copies are unaffected
  // Library-field reference metadata (the data-dictionary columns on the Fields
  // table). Like `enabled` and `categoryIds`, these describe the library ROW and
  // are not copied by value onto a form.
  systemName?: string      // library field only — stable machine identifier, curated in the DB; READ-ONLY, never sent on create/update
  // Library field only — the TENANT's reference name for this field: the
  // `weight` in `{weight}`, written by the "Field ID" column of the import
  // sheet and unique per tenant. Unlike `systemName` this one is settable.
  //
  // It is a RESOLUTION KEY, not the id of the field once placed on a form —
  // placement still mints a fresh id (see createFieldFromLibrary), because
  // `ProcessStepConnection` is unique on (tenantId, fieldId) alone and two
  // forms sharing a literal field id would share one process step's config.
  // The importer maps refName → minted id and rewrites the formula's tokens.
  refName?: string
  definition?: string      // library field only — what the field means
  example?: string         // library field only — a representative value
  isMrField?: boolean      // MR field — read-only, auto-populated from the patient record
  mrFieldKey?: MrFieldKey  // which Patient demographic column feeds this MR field
  libraryFieldId?: string  // provenance — id of the Field Library field this was copied from
  /**
   * Form JSON files only, never saved: the system name of the Field Library
   * field this one was placed from. Library ids differ between environments,
   * so `planFormImport` relinks `libraryFieldId` through this name when the id
   * in the file does not exist here. `buildFormCopyPayload` does not copy it.
   */
  librarySystemName?: string
  categoryIds?: string[]   // library field only — ids of the Field Categories it belongs to
  tableConfig?: TableConfig // for table only — inline grid configuration
  unlockedBy?: string | string[]  // intra-form sequencing — id(s) of the field(s) that must ALL be filled before this one unlocks (legacy: single string)
  visibility?: VisibilityRule // conditional visibility — show/hide (+ require) driven by other fields' values
  /**
   * Conditional notifications fired on submit — independent of visibility.
   *
   * A LIST: each entry pairs its own condition with its own message, delivery
   * methods and audience, so one field can notify QA on one outcome and the
   * pharmacy on another. Read through `notificationRules()` in
   * lib/formSections.ts, never directly — `notification` below is the original
   * single-rule shape and is still present in stored definitions.
   */
  notifications?: NotificationRule[]
  /** @deprecated Pre-list shape. Read via `notificationRules()`. */
  notification?: NotificationRule
  attachment?: AttachmentConfig // per-field file upload — absent means no attachment control
  constantText?: string    // for description only — the fixed text the author wrote
  unlockGate?: FormulaGate     // formula-driven unlock — supersedes `unlockedBy` when set
  signatureGate?: FormulaGate  // signature fields only — ANDed with `requiredGroup`; must pass to sign
  oos?: OosConfig              // number/text/textarea/dropdown/checkbox/date (lib/oos.ts)
}

/** Form classification / workflow phase. Independent of procedure flow. */
export type FormCategory =
  | 'clinical'
  | 'production'
  | 'patient_setup'
  | 'logistics'
  | 'lab_work'
  | 'qc_release'
  | 'infusion'
  | 'kit'
  | 'anchored'
  | 'exception'
  | 'capa'
  | 'inventory'
  // Cleaning module (z8nrz7ewt1): the forms an Equipment procedure and a
  // Cleaning procedure are built from.
  | 'equipment'
  | 'cleaning'
  // Collecting cells from a patient or donor. The Collection page lists
  // submissions of these forms only (z8nrz7f9j7).
  | 'collection'

export const FORM_CATEGORY_LABELS: Record<FormCategory, string> = {
  clinical: 'Clinical',
  production: 'Production',
  patient_setup: 'Patient Setup',
  logistics: 'Logistics',
  lab_work: 'Lab Work',
  qc_release: 'QC/Release',
  kit: 'Kit',
  infusion: 'Infusion',
  anchored: 'Anchored',
  exception: 'Exception',
  capa: 'CAPA',
  inventory: 'Inventory',
  equipment: 'Equipment',
  cleaning: 'Cleaning',
  collection: 'Collection',
}

/**
 * Form types in the order they are OFFERED to people — alphabetical by the
 * visible label, not by the order the union happens to be declared in.
 *
 * `Object.keys` returns declaration order, which put Clinical first and
 * Anchored last for no reason a user could infer. Sorting here rather than at
 * each call site means a newly added type lands in the right place everywhere
 * at once, instead of silently appending to the bottom of three pickers.
 */
export const FORM_CATEGORY_OPTIONS: FormCategory[] = (
  Object.keys(FORM_CATEGORY_LABELS) as FormCategory[]
).sort((a, b) => FORM_CATEGORY_LABELS[a].localeCompare(FORM_CATEGORY_LABELS[b]))

/**
 * Hex color per form type. Single source of truth for the Form Type badges and
 * the procedure-page step-row backgrounds. Clinical/Production use neutral slates so
 * they stay visually distinct from the vibrant workflow-phase colors.
 */
export const FORM_CATEGORY_COLORS: Record<FormCategory, string> = {
  clinical: '#2563eb', // blue
  production: '#9333ea', // purple
  patient_setup: '#5ba8a0',
  logistics: '#5eace2',
  lab_work: '#f19f87',
  qc_release: '#b09cd9',
  kit: '#c8a02c', // olive/amber — matches the Kit swatch in the form-type picker
  infusion: '#7ab99a',
  anchored: '#dc2626', // red
  exception: '#e57c23', // orange — stands out for unexpected scenarios
  capa: '#d946ef', // fuchsia — distinct from other workflow colors
  inventory: '#475569', // slate — reads as "warehouse", distinct from every other hue here
  equipment: '#0e7490', // deep cyan — machinery, darker than Logistics' light blue
  cleaning: '#16a34a', // green — "clean", far more saturated than Infusion's sage
  collection: '#be185d', // deep pink — blood/cell collection, distinct from Anchored's red
}

/** Input types for a table column default. */
export type TableColumnType =
  | 'text'
  | 'number'
  | 'date'
  | 'datetime'
  | 'dropdown'
  | 'checkbox'
  // Like the standalone `image` field type, an image CELL stores its picture as
  // a FormAttachment — keyed on the cell's own composite key rather than a
  // field id — and holds nothing in the values map.
  | 'image'
  // Fixed text, written per CELL by the author — see `constantText` on
  // TableCellConfig. The column type only supplies the default.
  | 'description'
  // A column default of 'calculated' means every cell in it starts out
  // calculated; each row's actual formula is still authored per cell (in
  // TableCellConfig.formula), since a formula necessarily differs per row.
  | 'calculated'

/** Cell types — kept as its own alias so call sites can name "the type of a
 *  resolved cell" distinctly from "a column's default", even though every
 *  TableColumnType member is a valid cell type. */
export type TableCellType = TableColumnType

/** A column in a table-layout form. `columnType` is the DEFAULT for its cells. */
export interface TableColumn {
  id: string
  label: string
  columnType: TableColumnType
  options?: string[] // dropdown only
  /** Specification for this column's cells of the column's own type. A cell's own `oos` wins. */
  oos?: OosConfig
  /**
   * The laboratory (LIS) stage this column's cells belongs to: a stage NAME, as an order
   * records it. A lab result mapped here is written only for an order placed at
   * this stage. Absent means no stage — the default, written whatever the stage
   * unless another destination for the same value is set to the order's stage.
   */
  lisStage?: string | null
  /** The LIS test code this column's cells are connected to. See `FieldDefinition.lisTestCode`. */
  lisTestCode?: string | null
}

/** A predefined row (the left-hand header) in a table-layout form. */
export interface TableRow {
  id: string
  label: string
}

/**
 * Per-cell override. The authoritative type of a cell is `type` here, falling
 * back to its column's `columnType` when absent. Stored only for cells that
 * actually differ from their column default — see lib/tableForm.ts.
 */
export interface TableCellConfig {
  type?: TableCellType
  options?: string[]                                    // dropdown override
  formula?: string                                      // calculated: {rowId__colId} tokens
  displayFormat?: 'number' | 'decimal2' | 'decimal3' | 'date' | 'datetime' // calculated formatting
  anchored?: boolean                                    // surface this cell in the procedure header
  oos?: OosConfig                                       // specification for this cell; overrides the column's
  /**
   * Fixed text for a `description` cell, written by the author.
   *
   * Per CELL rather than per column: a column of descriptions whose every row
   * said the same thing would be a column of one repeated sentence. The column
   * type declares that the cells ARE descriptions; each cell carries its own
   * words.
   */
  constantText?: string
  /**
   * Overrides the column's LIS stage for this one cell. The KEY's presence is
   * the override: absent inherits the column, `null` means no stage (any order),
   * a name narrows to that stage. See `lisStageOfTarget` in lib/lis/fieldStage.ts.
   */
  lisStage?: string | null
  /** Overrides the column's LIS test code for this cell, with the same KEY rule as `lisStage`. */
  lisTestCode?: string | null
  /**
   * The Field Library field this cell IS — chosen in the builder's Cell types
   * grid, the cell's counterpart of a placed field's `libraryFieldId`. Linking
   * also gives the cell the field's type. Anything that reads a library field
   * reads the cell the same way: Split and Pool find their inputs by the field's
   * system name (`tnc_per_ml`, …). Absent = an ordinary cell.
   */
  libraryFieldId?: string
  /**
   * Form JSON files only: the system name behind `libraryFieldId`, so an import
   * into another environment can relink the cell. Stripped by `planFormImport`.
   */
  librarySystemName?: string
  /** A greyed cell that takes no value. Absent = an ordinary cell. */
  disabled?: boolean
}

export interface TableConfig {
  /** Header shown above the left-most row-label column, e.g. "Year". */
  rowHeaderLabel: string
  columns: TableColumn[]
  rows: TableRow[]
  /** Allow the person filling the form to append extra rows at fill time. */
  allowAddRows?: boolean
  /** Per-cell overrides keyed by `${rowId}__${colId}`. Sparse — defaults inherit the column. */
  cells?: Record<string, TableCellConfig>
  /** Show the left-most row-label column. Absent = true, the layout before this existed. */
  showRowHeader?: boolean
  /** Show the column-label header row. Absent = true, the layout before this existed. */
  showColumnHeader?: boolean
}

// ─── Label layout (z8nrz7djyx) ────────────────────────────────────────────────
// Where a field's label sits relative to its control. Resolution and the
// width presets live in lib/fieldLayout.ts. Mirrors cgt-backend
// form-definitions/dto/create-form-definition.dto.ts (LABEL_POSITIONS/LABEL_WIDTHS).

export type LabelPosition = 'above' | 'left' | 'right'
export type LabelWidth = 'narrow' | 'medium' | 'wide'

/** The form-wide label default. A field's own labelPosition/labelWidth win over it. */
export interface FormFieldLayout {
  labelPosition?: LabelPosition
  labelWidth?: LabelWidth
}

/**
 * Width a section occupies in a builder/renderer row. Sections pack
 * left-to-right by width and wrap: full=full row, two halves share a row,
 * three thirds, four quarters (12-col grid: full=12, half=6, third=4, quarter=3).
 */
export type SectionWidth = 'full' | 'half' | 'third' | 'quarter'

/** Predefined header background tints for a section (none = default muted look). */
export type SectionColor = 'none' | 'slate' | 'blue' | 'green' | 'purple' | 'amber' | 'rose'

/**
 * A layout container that owns an ordered list of fields. Fields stack
 * vertically within the section; the multi-column look comes from placing
 * narrow sections (third/quarter) side by side.
 */
export interface SectionDefinition {
  id: string
  title: string
  width: SectionWidth
  /** Optional tint for the section header strip. Defaults to 'none' (muted). */
  headerColor?: SectionColor
  order: number            // section order within the form
  fields: FieldDefinition[]
  // Section-level sequencing: when true, this section stays locked at fill time
  // until the immediately-preceding section's required fields are all filled.
  // No effect on the first section (nothing precedes it).
  gatedByPrevious?: boolean
  // Multi-prerequisite section sequencing: id(s) of EARLIER sections that must ALL
  // be complete before this one unlocks at fill time. Supersedes gatedByPrevious
  // when set. Accepts a single id string (legacy) or an array; normalized via
  // sectionPrerequisiteIds().
  unlockedBySections?: string | string[]
  // Conditional visibility — show/hide this whole section driven by other
  // fields' values (sources must live outside this section).
  visibility?: VisibilityRule
}

export interface FormDefinition {
  id: string
  name: string
  /**
   * Permanent human-readable identifier (normalized name + 3 unique chars,
   * e.g. "patient-chart-x7k"). Lineage-level — shared by every version of the
   * family — and the namespace for cross-form references ({handle.fieldId},
   * see lib/crossForm.ts). Set once at creation; renames never change it.
   * Optional only for legacy mock data.
   */
  handle?: string
  /** Clinical vs production. This is the builder's "Form Type" field. */
  category: FormCategory
  active?: boolean
  /** True only for definitions created by the e2e suite (stamped server-side from the X-E2E-Test header); the e2e cleanup teardown deletes flagged definitions. */
  isTestData?: boolean
  /** LEGACY workflow stage — optional, no longer set by the builder. */
  formType?: CgtFormType
  /** Version within the family — minted only by the signed save-as-version flow. */
  version: number
  /** User-defined version label; falls back to V{version} when absent. */
  versionName?: string
  /** Groups every version of one form (= the first version's id). Absent only on legacy mock data — fall back to `id`. */
  familyId?: string
  /** Refreshed on both the old and new row on every version event. */
  lastVersionUpdateAt?: string | null
  /** Server annotation (list endpoint): the newest ACTIVE version of this family when strictly newer than this row. */
  latestActiveVersion?: { id: string; version: number } | null
  /** Ordered layout containers; each owns its fields. See getSections()/allFields() in lib/formSections. */
  sections: SectionDefinition[]
  /** LEGACY — superseded by procedure sequence predecessors. */
  predecessorFormTypes?: CgtFormType[]
  /** Action labels (matching FormActionBar ACTIONS) shown in the patient procedure action menu. Absent/empty = no actions shown. */
  allowedActions?: string[]
  /**
   * Default number of performances for a step running this form, when
   * `allowedActions` contains `Repeat` (z8nrz7dkkc).
   *
   * null / absent = does not repeat. Deliberately distinct from 1: a form that
   * never opted into Repeat has no opinion to seed a template step with, while
   * an explicit 1 is someone choosing "once". Unchecking Repeat sends null, and
   * the server clears the column — the count never outlives the action.
   *
   * A DEFAULT only. The live count lives on the template step, which may
   * override it.
   */
  defaultRepeatCount?: number | null
  /**
   * Procedure Template the builder resolves this form's cross-form references
   * against. null / absent = None (cross-form referencing off).
   */
  crossFormTemplateId?: string | null
  /** Whether the Create Product flow must capture/show patient identity on the label. Defaults true when absent; forms like kits set it false. */
  requiresPatientHistory?: boolean
  /**
   * Formula-driven whole-form completion gate: must evaluate true (or be absent) before Complete Form succeeds.
   * Checked client-side AND re-checked server-side. Read back as null when there is none; on a save, an explicit
   * null clears it while an absent key keeps the stored gate (z8nrz7fqft).
   */
  completionGate?: FormulaGate | null
  /** UserGroup ids whose members may complete this form (z8nrz7dt45). Empty = anyone with submissions.complete. */
  completerGroupIds?: string[]
  /**
   * Form-wide label default (z8nrz7djyx). null / absent = the system default
   * (label left, narrow), which is how every form saved before this rendered.
   * On save, absent keeps the stored value and null clears it.
   */
  fieldLayout?: FormFieldLayout | null
  createdBy: string
  createdAt: string
  updatedAt: string
}

// ─── Form submission (patient-specific data) ──────────────────────────────────

// `skipped`: left unfilled on purpose from the form's "+" menu; read-only, and
// done for the run's flow. Unskip returns it to `active`.
export type SubmissionStatus = 'draft' | 'active' | 'pending_review' | 'completed' | 'cancelled' | 'skipped'

/** How a submission status reads on screen: "Pending review", not "pending_review". */
export function submissionStatusLabel(status: SubmissionStatus | string): string {
  if (status === 'pending_review') return 'Pending review'
  return status.charAt(0).toUpperCase() + status.slice(1)
}

/** Closed to edits: completed, cancelled, or waiting for its review signature. */
export function isSubmissionClosed(status: SubmissionStatus | string): boolean {
  return status === 'completed' || status === 'cancelled' || status === 'pending_review'
}

export interface FormSubmission {
  id: string
  formDefinitionId: string
  /** LEGACY/denormalized — optional, may be absent for procedure-era forms. */
  formType?: CgtFormType
  /**
   * Null when the owning procedure has no patient yet — denormalised from it,
   * so it is null for the same reason (z8nrz7ckj9).
   */
  patientMrn: string | null // links all forms for a patient
  procedureId?: string | null
  /**
   * The product(s) of the procedure this form is about, chosen on the form bar's
   * "P" (z8nrz7fxub). Empty or absent: nobody chose (a single product in hand
   * is named without a choice).
   */
  workingProductIds?: string[]
  isAdHoc?: boolean
  addedFromStepId?: string | null
  /** Uploaded form (z8nrz7f030): locked until the step it was added from is completed. */
  waitsForAddedFromStep?: boolean
  isTestData?: boolean
  /**
   * `pending_review` (z8nrz7dt41): filled in and signed off, read-only, and
   * waiting for a reviewer's signature. NOT done — only `completed` is.
   */
  status: SubmissionStatus
  values: Record<string, unknown> // field id → value
  submittedBy?: string
  submittedAt?: string
  /** Who completed the form (audit actor) and when. */
  completedBy?: string | null
  completedAt?: string | null
  completedByUserId?: string | null
  /** Snapshotted at completion: whether the form had Review fields (z8nrz7dt41). */
  reviewRequired?: boolean
  /** When the last review was signed and the form became completed. */
  reviewedAt?: string | null
  createdAt: string
  updatedAt: string
}

// ─── Audit record (written on every field change) ────────────────────────────

export interface AuditEntry {
  id: string
  submissionId: string
  fieldId: string
  fieldLabel: string
  oldValue: unknown
  newValue: unknown
  changedBy: string
  changedAt: string
  reason?: string // 21 CFR Part 11 — required when changing a signed field
}

// ─── Procedure templates & form sequence ──────────────────────────────────────
//
// A ProcedureTemplate holds an ordered list of forms via ProcedureTemplateSequenceStep
// rows. The same FormDefinition can appear in several templates with different
// order / predecessors. Flow is defined here, not on the form template.

export type ProcedureEdgeType = 'start' | 'finish'

/** Row returned by `GET /procedure-templates`. */
export interface ProcedureTemplateListItem {
  id: string
  name: string
  description?: string | null
  productCode?: string | null
  type?: ProcedureType
  isTestData?: boolean
  /** Groups every version of one template (= the first version's id). */
  familyId?: string
  version?: number
  active?: boolean
  /** When true, this version is auto-selected for procedures created by external services (e.g. Epic). At most one version per tenant may be true. */
  autoSelect?: boolean
  /**
   * Days after its scheduled start that a run from this template is due.
   * Null/absent = no default, so the due date is left alone rather than
   * derived. Zero is meaningful: due the same day.
   */
  dueOffsetDays?: number | null
  lastVersionUpdateAt?: string | null
  updatedAt: string
  /**
   * How many forms this template version contains, counted by the server.
   *
   * Optional because the shape mirrors Prisma's `_count`, and a caller reading
   * a cached or hand-built row may not have it — render 0, never blank, so an
   * empty template is distinguishable from a missing count.
   */
  _count?: { sequenceSteps: number }
}

/** A predecessor reference on a step (as returned by the detail endpoint). */
export interface StepPredecessor {
  stepId: string
  sortOrder: number
  formName: string
}

/** One step in a procedure template's sequence, as returned by `GET /procedure-templates/:id`. */
export interface ProcedureTemplateSequenceStep {
  stepId: string
  sortOrder: number
  formDefinitionId: string
  formName: string
  /** The form's permanent handle — namespace for cross-form references. */
  formHandle?: string
  formType?: CgtFormType | null
  /** Version-control info about the referenced form (86eybvqgn). */
  formFamilyId?: string
  formVersion?: number
  formActive?: boolean
  /** The newest ACTIVE version of the form's family when strictly newer than the referenced one — drives the "New form version available" check. */
  latestActiveForm?: { id: string; version: number } | null
  /**
   * How many times this step is performed in one run (z8nrz7dkkc).
   *
   * null = does not repeat, and is NOT normalised to 1 on the wire because the
   * Sequence Editor tells them apart: null shows no input at all, 1 shows an
   * input reading 1.
   */
  repeatCount?: number | null
  predecessorStepIds: string[]
  predecessors: StepPredecessor[]
}

export interface ProcedureTemplateStepEdge {
  predecessorStepId: string
  successorStepId: string
  triggerFieldId?: string | null
  edgeType: ProcedureEdgeType
  minLagDays?: number | null
  maxLagDays?: number | null
  condition?: string | null
}

/** Full procedure template with its ordered sequence — `GET /procedure-templates/:id`. */
export interface ProcedureTemplateDetail {
  id: string
  name: string
  description?: string | null
  productCode?: string | null
  type?: ProcedureType
  familyId?: string
  version?: number
  active?: boolean
  autoSelect?: boolean
  /** Days after the scheduled start a run is due. Null = no default. */
  dueOffsetDays?: number | null
  lastVersionUpdateAt?: string | null
  /**
   * Why this version's sequence cannot be edited, or null/absent when it can
   * (z8nrz7ckjj). A version is frozen once a newer one exists in its family, or
   * once it is inactive — the server refuses `replaceSequence` on either, and
   * this is the same rule stated up front so the editor never lets someone
   * build a sequence it will then reject.
   */
  sequenceFrozenReason?: string | null
  createdBy: string
  createdAt: string
  updatedAt: string
  steps: ProcedureTemplateSequenceStep[]
  edges: ProcedureTemplateStepEdge[]
}

/** One version-control audit event (forms + templates) — append-only. */
export interface VersionAuditEntry {
  id: string
  entityType: 'form_definition' | 'procedure_template'
  familyId: string
  entityId: string
  oldValue?: Record<string, unknown> | null
  newValue?: Record<string, unknown> | null
  eventType:
    | 'version_created'
    | 'updated_in_place'
    | 'form_ref_updated'
    | 'activated'
    | 'inactivated'
    | 'cascade_inactivated'
  reason?: string | null
  signedBy: string
  changedAt: string
}

export interface StepPredecessorInput {
  predecessorSortOrder: number
  triggerFieldId?: string
  edgeType?: ProcedureEdgeType
  minLagDays?: number
  maxLagDays?: number
  condition?: string
}

/** One step in the `PUT /procedure-templates/:id/sequence` payload. */
export interface SequenceStepInput {
  formDefinitionId: string
  sortOrder: number
  /**
   * Per-template repeat override (z8nrz7dkkc).
   *
   * Omit to let the server seed the step from the form's `defaultRepeatCount`;
   * send null to mean "does not repeat". The server ignores a count on a form
   * whose `Repeat` action is off, so the editor cannot persist an orphan.
   */
  repeatCount?: number | null
  predecessors?: StepPredecessorInput[]
  /** @deprecated Use predecessors[] */
  predecessorSortOrders?: number[]
}

/** The workflow type a ProcedureTemplate authors. */
/**
 * Wire values for a procedure template's type.
 *
 * These are the Prisma enum MEMBER names, which is what the API validates
 * against — not the stored column values. `KitCreation` is stored as
 * "KIT Creation" via an `@map`, so sending the pretty string is rejected with
 * "type must be one of the following values: …". Use PROCEDURE_TYPE_LABELS for
 * anything user-facing.
 */
/**
 * There is deliberately no `CAPA`. A CAPA is a capa-category FORM raised inside a
 * run already in progress, not a workflow of its own — see `CapaRecord`.
 */
export type ProcedureType = 'Transplant' | 'MES' | 'KitCreation' | 'Cleaning' | 'Inventory' | 'Equipment'

export const PROCEDURE_TYPE_LABELS: Record<ProcedureType, string> = {
  Transplant: 'Transplant',
  MES: 'MES',
  KitCreation: 'KIT Creation',
  Cleaning: 'Cleaning',
  Inventory: 'Inventory',
  // Equipment maintenance — the counterpart of Cleaning (z8nrz7ewt1).
  Equipment: 'Equipment',
}

export const PROCEDURE_TYPES = Object.keys(PROCEDURE_TYPE_LABELS) as ProcedureType[]

/**
 * Procedure types in the order they are OFFERED to people — alphabetical by
 * label, as `FORM_CATEGORY_OPTIONS` is, so a newly added type lands in the
 * right place in every picker and filter at once (z8nrz7ewt4). Data
 * breakdowns keep `PROCEDURE_TYPES`.
 */
export const PROCEDURE_TYPE_OPTIONS: ProcedureType[] = [...PROCEDURE_TYPES].sort((a, b) =>
  PROCEDURE_TYPE_LABELS[a].localeCompare(PROCEDURE_TYPE_LABELS[b]),
)

export type ProcedureStatus = 'in_progress' | 'completed' | 'cancelled'

/**
 * What the procedure lists' `?status=` filter accepts: the column values plus
 * `pending_review` — an in-progress run with a form waiting for its reviews
 * (z8nrz7dt41). Never stored on a procedure; the server derives it.
 */
export type ProcedureListStatus = ProcedureStatus | 'pending_review'

/** A user as shown in Assign To controls — display name already resolved. */
export interface ProcedureAssignee {
  id: string
  name: string
}

/**
 * Procedure-level "Assign To", always DERIVED from the per-form assignments
 * (never stored): every form the same user → that user; anything mixed or
 * partial → multiple; no assignments → none.
 */
export type ProcedureAssignedTo =
  | { kind: 'user'; user: ProcedureAssignee }
  | { kind: 'multiple' }
  | { kind: 'none' }

/** A patient's live procedure — `GET /procedures`. */
/**
 * One CAPA raised inside a run.
 *
 * The CAPA *is* the capa-category form, so this is a form submission seen
 * through the CAPA lens: the Event ID it was minted with, the form that was
 * picked when it was raised, and who raised it.
 */
export interface CapaRecord {
  /** The form submission's id — what the CAPA page links to. */
  id: string
  /**
   * `E_ID1`, `E_ID42` — minted server-side per tenant, never supplied. Null only
   * on a CAPA raised before Event IDs existed; blank is correct there, not
   * missing data.
   *
   * Named `capaEventId` and not `eventId` because the domain has several other
   * kinds of event (calendar, maintenance, instrument) whose ids this is not.
   */
  capaEventId: string | null
  /** The form's own lifecycle, spelled as on FormSubmission. */
  status: SubmissionStatus
  /**
   * The ACTOR who raised it — `actorOf(user)` server-side, so an email address
   * in practice. Not a name: see `createdByName`, and `capaAuthor` for which of
   * the two a column should show.
   */
  createdBy: string
  /**
   * `createdBy` resolved to a person's name server-side, or null when no user
   * matches it. Optional so an older backend simply reads as unresolved.
   */
  createdByName?: string | null
  createdAt: string
  /**
   * When the CAPA was closed, or null while it is still open.
   *
   * Separate from `createdAt` because the stat cards ask two different
   * questions of the same record — how many were raised this week, how many
   * were closed this week. Absent on an older backend, which reads as "not
   * closed" rather than as a wrong date.
   */
  completedAt?: string | null
  formDefinitionId: string
  formName: string
  formVersion: number
}

/** Requirement lines a run needs, and how many of them it holds today. */
export interface ProcedureResourceSummary {
  required: number
  reserved: number
}

/**
 * What the Overall Status board shows about a run beyond the list row, as
 * `GET /procedures?summary=true` returns it.
 *
 * `required` comes from the template's process fields; `reserved` from what
 * the run's started forms hold. A form nobody has opened holds nothing, so a
 * partly started run can read "1 of 3".
 */
export interface ProcedureSummaryStep {
  name: string
  status: 'completed' | 'skipped' | 'in_progress' | 'not_started'
}

export interface ProcedureSummary {
  /** Template steps; a step is complete when the run completed its form family. */
  progress: { completed: number; total: number; hasActive: boolean }
  /** Every template step in template order. Absent from an older backend. */
  steps?: ProcedureSummaryStep[]
  inventory: ProcedureResourceSummary
  equipment: ProcedureResourceSummary
  capa: { count: number; latestSubmissionId: string | null }
  /** A form in the template offers Create Product / Display Patient History. */
  actions: { product: boolean; history: boolean }
  productCount: number
  /** The patient's other runs. */
  historyCount: number
}

/**
 * A product whose DIN matched the procedures list's search, annotated onto the
 * case it belongs to (z8nrz7dkzy AC2.3).
 *
 * A split's aliquots SHARE the parent's DIN — the donation identity never
 * changes — and are distinguished by the division suffix carried in
 * `descriptionCode` (`A0`, `B0`, then `Aa`, `Ab`). The DIN alone therefore
 * cannot say which bag matched, which is why the suffix and `isAliquot` travel
 * with it.
 */
export interface ProductMatch {
  din: string
  /** `Product.productDescriptionCode` — carries a split's division suffix. */
  descriptionCode: string
  /** True for an aliquot produced by a split, false for a received parent. */
  isAliquot: boolean
}

export interface Procedure {
  id: string
  /**
   * Null when the procedure has no patient YET. An MES order gets its patient
   * from an Epic order, so it exists before one is known (z8nrz7ckj9).
   *
   * Also not always an MRN even when set: a kit run writes its supply id here.
   */
  patientMrn: string | null
  procedureTemplateId: string
  status: ProcedureStatus
  isTestData?: boolean
  startedAt: string
  /**
   * Planned start of the procedure — an ISO INSTANT carrying a time of day
   * since z8nrz7cthg, not the calendar date it used to be. Equipment is booked
   * to the minute, and this is what the reservation automation reads.
   *
   * Rows created before that change hold exactly midnight UTC, which is how
   * `hasTimeOfDay` in `src/lib/planningDates.ts` tells "date only" apart from a
   * real time. Null when unset.
   */
  scheduledDate: string | null
  /** Target completion, same instant semantics as `scheduledDate`. Null when unset. */
  dueDate: string | null
  /**
   * The due date was typed by a person rather than derived from the template's
   * offset. An overridden date survives re-scheduling; a derived one follows
   * the schedule. Absent on an older backend, which reads as derived.
   */
  dueDateOverridden?: boolean
  /**
   * Operator-facing work order number (WO-26-0043), minted server-side for
   * Transplant procedures only. Null on every other type and on rows predating
   * work orders — blank is correct there, not missing data.
   */
  workOrderNumber: string | null
  /**
   * Operator-facing manufacturing number (MFG-000042), minted server-side for
   * MES orders only, never supplied. Null on every other type — blank is correct
   * there, not missing data (z8nrz7ckj9).
   */
  mfgNumber: string | null
  /**
   * The CAPAs raised inside this run, on `?capaOnly=true` responses only.
   *
   * A CAPA is a capa-category FORM attached to a running procedure, so a run can
   * hold several and each is its own row on the CAPA page. Absent on every other
   * list — the procedures page has no column for them and pages 20 rows at a
   * time.
   */
  capaRecords?: CapaRecord[]
  /** Board data, on `?summary=true` responses only. See `ProcedureSummary`. */
  summary?: ProcedureSummary
  /**
   * Units this procedure produces. Multiplies every step's inventory
   * requirement server-side; 1 for all patient care. Set at creation only.
   */
  quantity: number
  /**
   * Who a Kit or MES run produces material for (z8nrz7e714). Optional; absent
   * on every other type, and null once the customer entry is deleted.
   */
  customer?: { id: string; code: string; name: string } | null
  /**
   * The Product Code an MES run makes (z8nrz7fvhx). Optional; absent on every
   * other type and on an older backend. Both descriptions come, and each user's
   * preference picks which one shows first.
   */
  mesProduct?: MesProductSummary | null
  createdBy: string
  createdAt: string
  updatedAt: string
  /**
   * `type` gates template-kind-specific affordances (pick lists are KitCreation-only).
   * `version` is the run's own template version; absent from an older backend.
   */
  procedureTemplate: { id: string; name: string; type?: ProcedureType | null; version?: number }
  /** Derived procedure-level Assign To — present on list responses. */
  assignedTo?: ProcedureAssignedTo
  /**
   * Whether this procedure's template has a Process field anywhere — i.e.
   * whether it can have a bill of materials at all. Gates the pick-list
   * control: "we should see plus only for Procedure where Process Step is
   * found". List responses only; optional so an older backend reads as
   * "unknown" rather than "no".
   */
  hasProcessStep?: boolean
  /**
   * A form of this run is completed and waiting for its reviews (z8nrz7dt41):
   * every list shows the run as "Pending review". List responses only; absent
   * from an older backend, which reads as no.
   */
  hasPendingReview?: boolean
  /**
   * Which product a DIN search matched (z8nrz7dkzy AC2.3).
   *
   * Server-set, and ONLY on a row the search reached through a product —
   * absent on one reached by patient, MRN or date of birth, so its presence is
   * what says "this row is here because of a product". Optional also covers an
   * older backend that does not send it.
   */
  productMatches?: ProductMatch[]
  /**
   * Row progress, on `?progress=true` responses only (z8nrz7e8kw): what the
   * procedures tables used to fetch one journey per row for, counted the same
   * way (each repeat a unit). Absent from an older backend; the tables then
   * fall back to the per-row journeys.
   */
  progress?: { completed: number; total: number; hasActive: boolean }
  /**
   * The patient's display name from their anchored form values, on
   * `?patientNames=true` responses only (z8nrz7e8kw). Null when nothing names
   * them or the run has no patient; absent from an older backend.
   */
  patientName?: string | null
}

// ─── Procedure scheduling (capacity calendar) ────────────────────────────────

/** A bookable resource — a piece of equipment or a staff member. */
export interface SchedulingResource {
  id: string
  name: string
  kind: 'equipment' | 'staff'
  /** Maximum hours this resource can be booked for in a single day. */
  capacityHoursPerDay: number
}

/** How many hours a step books against a single resource. */
export interface StepResourceAllocation {
  resourceId: string
  hours: number
}

/**
 * The scheduling metadata for one procedure step. Kept separate from
 * ProcedureTemplateSequenceStep so the form-sequence types are untouched;
 * joined to a step by `stepId`.
 */
export interface StepScheduleTemplate {
  stepId: string
  /** Total elapsed hours the step occupies on its scheduled day. */
  durationHours: number
  allocations: StepResourceAllocation[]
}

/** One placed step on the calendar for a specific procedure. */
export interface ScheduledBlock {
  id: string
  procedureId: string
  stepId: string
  label: string
  formType?: CgtFormType
  /** Days from the schedule's anchor date (0 = anchor day). */
  dayOffset: number
  /** ISO date (YYYY-MM-DD) the block is scheduled on. */
  date: string
  durationHours: number
  allocations: StepResourceAllocation[]
  hasConflict: boolean
  conflictReasons: string[]
}

/** A resource over-allocated on a given day. */
export interface ResourceConflict {
  resourceId: string
  /** ISO date (YYYY-MM-DD). */
  date: string
  allocatedHours: number
  capacityHours: number
  blockIds: string[]
}

/** The full computed schedule for one procedure. */
export interface ProcedureSchedule {
  procedureId: string
  /** Carried through from the procedure, so null when it has no patient. */
  patientMrn: string | null
  procedureTemplateId: string
  /** ISO date (YYYY-MM-DD) the chain is anchored to. */
  anchorDate: string
  blocks: ScheduledBlock[]
  conflicts: ResourceConflict[]
}

// ─── Patient journey through a procedure template ─────────────────────────────

export type ProcedureStepStatus =
  | 'completed'
  // Left unfilled on purpose: done for the run, so what waits on it opens.
  | 'skipped'
  | 'in_progress'
  | 'not_started'
  | 'locked'

export interface JourneyBlocker {
  stepId: string
  formName: string
  reason: 'not_completed'
}

/**
 * One performance of a repeating step (z8nrz7dkkc).
 *
 * Each repeat is its own FormSubmission — independently filled, completed,
 * signed and audit-trailed, because under 21 CFR Part 11 a signature applies to
 * one performance of a form, not to a bundle of them.
 */
export interface ProcedureJourneyRepeat {
  /** 1-based. A 3x step has repeats 1, 2 and 3. */
  repeatIndex: number
  status: ProcedureStepStatus
  /** null until this particular repeat has been started. */
  submissionId: string | null
  /**
   * Who performs THIS repeat.
   *
   * Already resolved by the server: a repeat-specific assignment when one
   * exists, otherwise whoever holds the step as a whole. So a step assigned to
   * one person shows that person on every repeat, and reassigning a single
   * repeat changes only that row — the caller never has to merge the two.
   */
  assignee?: ProcedureAssignee | null
}

export interface ProcedureJourneyStep {
  stepId: string
  sortOrder: number
  formDefinitionId: string
  formName: string
  /** The form's permanent handle — namespace for cross-form references. */
  formHandle?: string
  formVersion?: number
  /**
   * The step's status, rolled up across its repeats: `completed` only when
   * EVERY repeat is, which is the same rule that unlocks the next step.
   */
  status: ProcedureStepStatus
  /**
   * The submission this step opens by default — the first repeat still needing
   * work, else the last. Repeat-aware callers should read `repeats` instead.
   */
  submissionId: string | null
  /**
   * How many times this step runs. Always present and always >= 1, so a caller
   * can test `repeatCount > 1` without knowing whether the column was null.
   */
  repeatCount?: number
  /**
   * Per-repeat detail, in order — present ONLY when the step actually repeats.
   *
   * A 1x step omits it entirely, which is what keeps every surface that
   * predates repeats rendering exactly as it does today.
   */
  repeats?: ProcedureJourneyRepeat[]
  blockedBy: JourneyBlocker[]
  /** Earliest this form may be filled (min-lag from predecessors). ISO or null. */
  availableAt?: string | null
  /** Deadline to complete this form (max-lag from predecessors). ISO or null. */
  dueAt?: string | null
  /** Assign To for this form on the procedure — null/absent = unassigned. Only
   *  populated when the journey was fetched with a procedureId. */
  assignee?: ProcedureAssignee | null
  /**
   * Whether this form carries a Process field — i.e. whether it can have a bill
   * of materials at all. Gates the per-form pick-list control. Optional so a
   * response from an older backend reads as "unknown" rather than "no".
   */
  hasProcessStep?: boolean
}

/** Response of `GET /procedure-templates/:id/journey?patientMrn=...`. */
export interface ProcedureJourney {
  /** `type` gates template-kind-specific affordances (pick lists are KitCreation-only). */
  procedureTemplate: { id: string; name: string; type?: ProcedureType | null }
  steps: ProcedureJourneyStep[]
}

// ─── Current user context ────────────────────────────────────────────────────

export interface CgtUser {
  id: string
  name: string
  groups: string[] // e.g. ['LabTechnician', 'MedicalDirector']
}

// ─── Product Codes (MES) ─────────────────────────────────────────────────────

/**
 * A Product Code (z8nrz7fvhx): a product an MES run makes, from the tenant's own
 * catalogue (Configure → Product Code). Not the ISBT 128 catalog (Configure →
 * Products CGT) and not a made product with a DIN (`/products`).
 *
 * Description 1 and 2 are typically the same description in two languages;
 * each user picks which one reads first (see src/lib/mesProducts.ts).
 */
export interface MesProductSummary {
  id: string
  code: string
  description1: string
  description2: string
}

/** A storage unit as a Product Code names it: "LN2-FRZ-03 · Cryo Room B". */
export interface MesProductStorageUnit {
  id: string
  label: string
  text: string
}

export interface MesProduct extends MesProductSummary {
  productType: string
  productNature: string
  clinicalOrCommercial: string
  dosageForm: string
  dosage: string
  temperatureSpecification: string
  quantityPerPrimaryPackaging: number | null
  containerType: string
  primaryPackagingDimensions: string
  storagePackagingDimensions: string
  adrIataClassification: string
  adrIataClassificationOther: string
  minTransportTemperature: string
  maxTransportTemperature: string
  storageTemperature: string
  dedicatedStorageUnit: MesProductStorageUnit | null
  mirrorStorageUnit: MesProductStorageUnit | null
  comments: string
  qualiosLink: string
  specificInstructions: string
  projectCode: string
  active: boolean
  updatedBy: string
  createdAt: string
  updatedAt: string
}

/** The selection fields, each with its own option list (not the forms' Option Sets). */
export type MesProductOptionField =
  | 'productType'
  | 'productNature'
  | 'clinicalOrCommercial'
  | 'dosageForm'
  | 'containerType'
  | 'adrIataClassification'
  | 'adrIataClassificationOther'
  | 'projectCode'

/** What the Product Code form chooses from (`GET /mes-products/reference`). */
export interface MesProductReference {
  lists: { field: MesProductOptionField; name: string; options: string[] }[]
  /** The ADR/IATA Classification option that opens "(if Other)". */
  adrIataOther: string
  storageUnits: MesProductStorageUnit[]
}

/** One choice of a Product Code option list; a product stores its label. */
export interface MesProductOption {
  id: string
  label: string
}

/**
 * A selection field's option list, edited from Configure → Product Code
 * (`GET /mes-products/option-lists`). Product Codes only: forms use Option Sets.
 */
export interface MesProductOptionList {
  field: MesProductOptionField
  /** The field's name on the Product Code screen, e.g. "Dosage Form". */
  name: string
  options: MesProductOption[]
  updatedBy: string | null
  updatedAt: string | null
}

/** A create or an edit. Storage units by id; null clears one. The code is create-only. */
export type MesProductPayload = Partial<
  Omit<MesProduct, 'id' | 'dedicatedStorageUnit' | 'mirrorStorageUnit' | 'updatedBy' | 'createdAt' | 'updatedAt'>
> & {
  dedicatedStorageUnitId?: string | null
  mirrorStorageUnitId?: string | null
}
